import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaStar, FaUserAlt, FaBed, FaExpand, FaFilter } from 'react-icons/fa';
import { rooms } from '../data/hotelData';
import './Rooms.css';

const categories = ['all', 'classic', 'deluxe', 'superior', 'suite'];

export default function RoomsPage() {
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('default');
  const [priceRange, setPriceRange] = useState(1000);

  const filtered = rooms
    .filter(r => filter === 'all' || r.category === filter)
    .filter(r => r.price <= priceRange)
    .sort((a, b) => {
      if (sort === 'price-asc') return a.price - b.price;
      if (sort === 'price-desc') return b.price - a.price;
      if (sort === 'rating') return b.rating - a.rating;
      return 0;
    });

  return (
    <main>
      <div className="page-banner">
        <div className="page-banner-content">
          <span className="section-badge">Accommodations</span>
          <h1>Rooms & Suites</h1>
          <p>Discover your perfect luxury sanctuary</p>
        </div>
      </div>

      <section className="section rooms-page">
        <div className="container">
          <div className="rooms-filters">
            <div className="filter-categories">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`filter-btn ${filter === cat ? 'active' : ''}`}
                  onClick={() => setFilter(cat)}
                >
                  {cat === 'all' ? 'All Rooms' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>

            <div className="filter-controls">
              <div className="price-filter">
                <label>Max Price: <strong>${priceRange}</strong>/night</label>
                <input
                  type="range"
                  min={100} max={1000} step={50}
                  value={priceRange}
                  onChange={e => setPriceRange(Number(e.target.value))}
                />
              </div>

              <div className="sort-filter">
                <FaFilter />
                <select value={sort} onChange={e => setSort(e.target.value)}>
                  <option value="default">Sort by Default</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="rating">Highest Rated</option>
                </select>
              </div>
            </div>
          </div>

          <p className="rooms-count">{filtered.length} room{filtered.length !== 1 ? 's' : ''} found</p>

          {filtered.length === 0 ? (
            <div className="rooms-empty">
              <p>No rooms match your criteria. Try adjusting the filters.</p>
              <button className="btn btn-outline" onClick={() => { setFilter('all'); setPriceRange(1000); }}>
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="rooms-grid">
              {filtered.map((room, i) => (
                <motion.div
                  key={room.id}
                  className="room-card-horizontal"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                >
                  <div className="room-h-image">
                    <img src={room.image} alt={room.name} loading="lazy" />
                    <span className="room-h-badge">{room.category}</span>
                  </div>

                  <div className="room-h-body">
                    <div className="room-h-top">
                      <div>
                        <h3 className="room-h-name">{room.name}</h3>
                        <div className="room-h-rating">
                          <FaStar style={{ color: '#fbbf24' }} />
                          <strong>{room.rating}</strong>
                          <span>({room.reviews} reviews)</span>
                        </div>
                      </div>
                      <div className="room-h-price">
                        <strong>${room.price}</strong>
                        <span>/night</span>
                      </div>
                    </div>

                    <div className="room-h-meta">
                      <span><FaExpand /> {room.size} m²</span>
                      <span><FaUserAlt /> {room.capacity} guests</span>
                      <span><FaBed /> {room.beds}</span>
                      <span>👁 {room.view}</span>
                    </div>

                    <p className="room-h-desc">{room.description}</p>

                    <div className="room-h-amenities">
                      {room.amenities.slice(0, 4).map(a => (
                        <span key={a} className="amenity-tag">{a}</span>
                      ))}
                      {room.amenities.length > 4 && (
                        <span className="amenity-tag">+{room.amenities.length - 4} more</span>
                      )}
                    </div>

                    <div className="room-h-actions">
                      <Link
                        to={`/rooms/${room.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className="btn btn-outline"
                      >
                        View Details
                      </Link>
                      <Link to="/booking" className="btn btn-primary">Book Now</Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
